<template>
    <PhoneNumber
        v-if="type === 'phone_number'"
        :id="id"
        :tabs="['phone-numbers.call-flow', 'phone-numbers.show']"
        @updated="update_screen"
        @click:cancel="$emit('click:cancel')"
        data-test-id="autocomplete-edit-phone-num"
    />
    <Extension
        v-else-if="type === 'extension'"
        :id="id"
        :tabs="['extensions.call-flow', 'extensions.show']"
        @updated="update_screen"
        @click:cancel="$emit('click:cancel')"
        data-test-id="autocomplete-edit-extension"
    />
    <div v-else-if="tabs && Object.keys(tabs).length">
        <w-tabs
            v-model="active_tab"
            grow
            centered
            center-active
            :ripple="false"
            slider-size="4"
            :show-arrows="true"
        >
            <v-tab v-for="tab in Object.keys(tabs)" :href="`#${tab}`">
                {{ conf[tab].title }}
            </v-tab>
        </w-tabs>
        <v-tabs-items v-if="tabs[active_tab]" v-model="active_tab" class="mt-5">
            <w-alert v-if="alert" :message="alert.message" :level="alert.level"/>
            <w-loader v-else-if="loading"/>
            <template v-else>
                <Route
                    v-if="'route' === active_tab && (tabs.route.id || tabs.route.value)"
                    :id="tabs.route.id"
                    :_route="tabs.route.value"
                    :disable_switcher="true"
                    @updated="update_screen"
                    @click:cancel="$emit('click:cancel')"
                    name="test"
                    data-test-id="autocomplete-edit-route"
                />
                <div v-if="'route' === active_tab && !tabs.route.id && !tabs.route.value">
                    {{ l.t('app.item-doesnt-have-route', 'This item doesn\'t have rotue') }}
                </div>
                <AccountSettings
                    v-if="active_tab === 'account_settings'"
                    :_account="JSON.parse(JSON.stringify(tabs.account_settings.value))"
                    @submitted="updateSettings"
                    @click:cancel="$emit('click:cancel')"
                    data-test-id="account-settings"
                />
                <AccountContacts
                    v-if="active_tab === 'account_contacts'"
                    :_account="JSON.parse(JSON.stringify(tabs.account_contacts.value))"
                    @submitted="updateContacts"
                    @click:cancel="$emit('click:cancel')"
                    data-test-id="account-contacts"
                />
            </template>
        </v-tabs-items>
    </div>
</template>
<script>
import AccountSettings from '../fragments/Account.vue';
import AccountContacts from '../fragments/AccountContacts.vue';
import { vueComponent, isNumeric } from '../../libs/helpers2';
import l from '../../libs/lang';
import Account from '../../models/Account';
import Extension from '../screens/extensions/show.vue';
import PhoneNumber from '../screens/phone-numbers/show.vue';

const conf = {
    extension: {
        title: l.t('app.settings', 'Settings'),
        additionalTabs: ['route'],
    },
    route: {
        title: l.t('app.call-handling', 'Call handling'),
    },
    phone_number: {
        title: l.t('app.settings', 'Settings'),
        additionalTabs: ['route'],
    },
    account_settings: {
        title: l.t('app.account-settings', 'Account settings'),
        additionalTabs: ['account_contacts'],
    },
    account_contacts: {
        title: l.t('app.account-contacts', 'Account contacts'),
        additionalTabs: ['account-settings'],
    }
};
export default {
    name: 'MultiResourceModalContent',
    props: {
        type: {
            type: String,
            required: true,
            validator: (data) => {
                if (!Object.keys(conf).includes(data)) {
                    console.error(`Supported types: ${Object.keys(conf).join(', ')}, ${data} provided.`);
                    return false;
                }
                return true;
            }
        },
        id: {
            type: [String, Number],
            required: false, // account settings and contacts dont need id
            validator: (data) => {
                if (data && !isNumeric(data)) {
                    console.error(`ID value for desired resource has to be a number, ${data} provided.`);
                    return false;
                }
                return true;
            },
        },
    },
    components: {
        Route: () => import('../screens/routes/show.vue'),
        RouteCreate: () => import('../screens/routes/create.vue'),
        Extension,
        PhoneNumber,
        AccountContacts,
        AccountSettings,
    },
    data() {
        return {
            l,
            active_tab: null,
            conf,
            loading: false,
            tabs: {},
            account: new Account(this.$session, vueComponent(this)),
            alert: null,

        };
    },
    async created() {
        if (this.$props.type && !['extension', 'phone_number'].includes(this.$props.type)) {
            const _conf = this.$data.conf[this.$props.type];
            this.$data.tabs = {};
            this.$data.tabs[this.$props.type] = {
                value: null,
            };
            if (_conf.additionalTabs) {
                for (const tab of _conf.additionalTabs) {
                    this.$data.tabs[tab] = {
                        value: null,
                    };
                }
            }
            await this.loadTabValues();
        }
    },
    methods: {
        async loadTabValues() {
            this.$data.loading = true;
            this.$data.tabs[this.$props.type].main_tab = true;
            const item = await this.getTabValue(this.$props.type, this.$props.id);
            this.$data.tabs[this.$props.type] = {
                value: item,
                id: item.id,
            };
            const _conf = this.$data.conf[this.$props.type];
            if (_conf.additionalTabs) {
                for (const tab of _conf.additionalTabs) {
                    if (tab === 'route' && item.route) {
                        this.$data.tabs[tab].id = item.route.id;
                    }
                    if (['account_settings', 'account_contacts'].includes(tab)) {
                        await this.$data.account.getItem();
                        this.$data.tabs[tab].value = this.$data.account.item;
                    }
                }
            }
            this.$data.loading = false;
        },
        update_screen(data) {
            this.$emit('updated', data);
        },
        async getTabValue(type, id) {
            if (['account_settings', 'account_contacts'].includes(type)) {
                await this.$data.account.getItem();
                return this.$data.account.item;
            }
            let url;
            switch (type) {
                case 'route':
                url = '/routes';
                break;
                case 'account_settings':
                url = '/';
                break;
                case 'account_contacts':
                url = '/';
                break;
                default:
                throw new Error('Unsupported type');
            }
            url = id ? `${url}/${id}` : url;
            return this.$session.get_item(url);
        },
        async updateSettings(data) {
            this.$data.loading = true;
            try {
                await this.$data.account.updateSettings(data);
                this.$global_emitter.$emit('session-user-updated');
            } catch (err) {
                this.$data.account.validation_error(err);
            }
            this.$data.alert = this.$data.account.alert;
            this.$data.loading = false;
        },
        updateContacts(data) {
            this.$data.account.item = data;

            return this.$data.account.updateContacts(data);
        },
    }
};
</script>
